/**
 * 🔗 Slot-Redirect Configuration Component
 * Verwendet Slot-Domains als Sending-Domains für Macros
 */

import React, { useState, useEffect } from 'react';
import { Link, CheckCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const SlotRedirectConfig = ({ segment, onUpdate, isAdmin }) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [rotationMode, setRotationMode] = useState('round_robin'); // 'round_robin', 'random', 'sequential'
  
  const isEnabled = segment.config?.slot_redirects?.enabled || false;
  
  // Lade Slots beim Mount
  useEffect(() => {
    if (isAdmin && isEnabled) {
      loadSlots();
    }
  }, [isAdmin, isEnabled]);
  
  // Lade existierende Konfiguration
  useEffect(() => {
    const slotConfig = segment.config?.slot_redirects || {};
    if (slotConfig.enabled && slotConfig.slot) {
      setSelectedSlot(slotConfig.slot);
      setRotationMode(slotConfig.rotation_mode || 'round_robin');
    }
  }, [segment]);
  
  const loadSlots = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/slot-redirect/slots');
      const loadedSlots = response.data.data?.slots || [];
      setSlots(loadedSlots);
      if (loadedSlots.length > 0) {
        toast.success(`✅ ${loadedSlots.length} Redirect-Slots geladen`);
      }
    } catch (error) {
      console.error('Failed to load redirect slots:', error);
      toast.error('Fehler beim Laden der Slots');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEnable = () => {
    const updated = { ...segment };
    if (!updated.config) updated.config = {};
    updated.config.slot_redirects = { enabled: true };
    onUpdate(updated);
    loadSlots();
  };
  
  const handleDisable = () => {
    const updated = { ...segment };
    if (!updated.config) updated.config = {};
    updated.config.slot_redirects = { enabled: false };
    onUpdate(updated);
    toast.success('Slot-Redirects deaktiviert');
  };
  
  const handleSave = () => {
    if (!selectedSlot) {
      toast.error('Bitte wählen Sie einen Slot aus');
      return;
    }
    
    const config = {
      enabled: true,
      slot: selectedSlot,
      rotation_mode: rotationMode
    };
    
    const updated = { ...segment };
    if (!updated.config) updated.config = {};
    updated.config.slot_redirects = config;
    
    onUpdate(updated);
    toast.success(`Slot-Redirect konfiguriert: ${selectedSlot.name} (${selectedSlot.domain_count} Domains)`);
  };
  
  if (!isAdmin) return null;
  
  return (
    <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-700">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
          <Link className="h-4 w-4 mr-2 text-purple-600" />
          🔗 Slot-Redirects (Domain-Rotation)
        </h4>
        
        {!isEnabled ? (
          <button
            onClick={handleEnable}
            className="px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
          >
            Aktivieren
          </button>
        ) : (
          <button
            onClick={handleDisable}
            className="px-3 py-1 bg-gray-400 text-white rounded text-xs hover:bg-gray-500"
          >
            Deaktivieren
          </button>
        )}
      </div>
      
      {isEnabled && (
        <div className="space-y-3">
          {/* Slot-Auswahl */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
              Redirect-Slot auswählen:
            </label>
            <select
              value={slots.findIndex(s => s.name === selectedSlot?.name)}
              onChange={(e) => setSelectedSlot(slots[e.target.value])}
              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800"
              disabled={loading}
            >
              <option value="">-- Slot wählen --</option>
              {slots.map((slot, idx) => (
                <option key={idx} value={idx}>
                  {slot.type === 'landing' ? '🌐' : '🔀'} {slot.name} ({slot.type === 'landing' ? `Landing · ${slot.domain_count} Domains` : `${slot.domain_count} Domains`})
                </option>
              ))}
            </select>
            {loading && <div className="text-xs text-gray-500 mt-1">Lade Slots...</div>}
          </div>
          
          {/* Rotation-Modus */}
          {selectedSlot && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                Domain-Rotation:
              </label>
              <select
                value={rotationMode}
                onChange={(e) => setRotationMode(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800"
              >
                <option value="round_robin">🔄 Round-Robin (gleichmäßig)</option>
                <option value="random">🎲 Random (zufällig)</option>
                <option value="sequential">📊 Sequential (der Reihe nach)</option>
              </select>
            </div>
          )}
          
          {/* Info Box */}
          {selectedSlot && (
            <div className="bg-purple-100 dark:bg-purple-900/20 p-3 rounded text-xs text-purple-800 dark:text-purple-200">
              <div className="font-semibold mb-1">ℹ️ Slot-Redirect Placeholders:</div>
              <div className="space-y-1 font-mono">
                <div>• <strong>{'{{LOGO}}'}</strong> → https://klhd.de/logo (Beispiel)</div>
                <div>• <strong>{'{{CTA}}'}</strong> → https://merzi.de/cta (Beispiel)</div>
                <div>• <strong>{'{Domain}'}</strong> → kaluo.de (Beispiel)</div>
                <div>• <strong>{'{MixDomain}'}</strong> → https://app-xxx.fospa.de (Beispiel)</div>
              </div>
              <div className="mt-2 text-xs">
                <strong>{selectedSlot.domain_count} Domains</strong> (z.B. {selectedSlot.domains?.slice(0, 3).join(', ')}...) werden über Slots rotiert ({rotationMode})
              </div>
            </div>
          )}
          
          {/* Save Button */}
          {selectedSlot && (
            <button
              onClick={handleSave}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center justify-center space-x-2"
            >
              <CheckCircle className="h-4 w-4" />
              <span>Slot-Redirect speichern</span>
            </button>
          )}
        </div>
      )}
      
      {/* Status Anzeige */}
      {isEnabled && segment.config?.slot_redirects?.slot && (
        <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 rounded text-xs text-green-800 dark:text-green-200">
          ✅ {segment.config.slot_redirects.slot.name} - {segment.config.slot_redirects.slot.domain_count} Domains ({segment.config.slot_redirects.rotation_mode})
        </div>
      )}
    </div>
  );
};

export default SlotRedirectConfig;

